import { useEffect, useCallback } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { useLocation } from 'wouter';
import apiClient from '@/lib/api-client';

export function OnboardingTour() {
  const { t } = useTranslation('tour');
  const { user, refreshUser } = useAuth();
  const [location, setLocation] = useLocation();

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const dashboardPath = isAdmin ? '/admin/dashboard' : '/staff/dashboard';

  const markTourCompleted = useCallback(async () => {
    try {
      await apiClient.put('/v1/auth/tour-completed');
      refreshUser();
    } catch {
      // Silently fail — tour will show again next time
    }
  }, [refreshUser]);

  const buildSteps = useCallback((): DriveStep[] => {
    const steps: DriveStep[] = [
      {
        popover: {
          title: t('welcome.title'),
          description: t('welcome.description'),
        },
      },
    ];

    // Steps visible to both admin and staff
    const sharedSteps: DriveStep[] = [
      {
        element: '#tour-dashboard',
        popover: {
          title: t('dashboard.title'),
          description: t('dashboard.description'),
          side: 'right',
          align: 'start',
        },
      },
    ];

    // Staff-only steps — these anchor to the personal provider links, which only
    // appear in the staff sidebar (admins use the merged team Schedule/Bookings).
    const staffSteps: DriveStep[] = [
      {
        element: '#tour-my-schedule',
        popover: {
          title: t('mySchedule.title'),
          description: t('mySchedule.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-my-bookings',
        popover: {
          title: t('myBookings.title'),
          description: t('myBookings.description'),
          side: 'right',
          align: 'start',
        },
      },
    ];

    // Admin-only steps
    const adminSteps: DriveStep[] = [
      {
        element: '#tour-services',
        popover: {
          title: t('services.title'),
          description: t('services.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-team',
        popover: {
          title: t('team.title'),
          description: t('team.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-team-schedule',
        popover: {
          title: t('teamSchedule.title'),
          description: t('teamSchedule.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-team-bookings',
        popover: {
          title: t('teamBookings.title'),
          description: t('teamBookings.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-settings',
        popover: {
          title: t('settings.title'),
          description: t('settings.description'),
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '#tour-billing',
        popover: {
          title: t('billing.title'),
          description: t('billing.description'),
          side: 'right',
          align: 'start',
        },
      },
    ];

    steps.push(...sharedSteps);

    if (!isAdmin) {
      steps.push(...staffSteps);
    }

    if (isAdmin) {
      steps.push(...adminSteps);

      // Booking URL step (only on admin dashboard, conditional on element existing)
      if (document.getElementById('tour-booking-url')) {
        steps.push({
          element: '#tour-booking-url',
          popover: {
            title: t('bookingUrl.title'),
            description: t('bookingUrl.description'),
            side: 'top',
            align: 'start',
          },
        });
      }
    }

    // Final step
    steps.push({
      popover: {
        title: t('complete.title'),
        description: t('complete.description'),
      },
    });

    return steps;
  }, [isAdmin, t]);

  const runTour = useCallback(() => {
    if (!user) return;

    const steps = buildSteps();
    let completedFully = false;

    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'sloty-tour-popover',
      nextBtnText: t('buttons.next'),
      prevBtnText: t('buttons.prev'),
      doneBtnText: t('buttons.done'),
      progressText: '{{current}} / {{total}}',
      steps,
      onDestroyStarted: () => {
        // Check if we're on the last step (user clicked "Done")
        if (driverObj.isLastStep()) {
          completedFully = true;
        }
        markTourCompleted();
        driverObj.destroy();
      },
      onDestroyed: () => {
        if (completedFully) {
          setLocation(dashboardPath);
        }
      },
    });

    // On mobile the sidebar is hidden — open it before starting the tour
    window.dispatchEvent(new CustomEvent('onboarding-tour:open-sidebar'));

    setTimeout(() => {
      driverObj.drive();
    }, 500);
  }, [user, t, buildSteps, markTourCompleted, dashboardPath, setLocation]);

  useEffect(() => {
    if (!user) return;
    if (!isAdmin && !isStaff) return;
    if (location !== dashboardPath) return;
    if (user.tourCompletedAt) return;

    // Wait for sidebar elements to render
    const timeout = setTimeout(() => {
      const checkEl = isAdmin ? 'tour-services' : 'tour-my-schedule';
      if (!document.getElementById(checkEl)) return;
      runTour();
    }, 1500);

    return () => clearTimeout(timeout);
  }, [location, user, isAdmin, isStaff, dashboardPath, runTour]);

  return null;
}
